import axios from 'axios';
import { BF_PLEDGE_API_MESSAGES } from '@/constants/MyGiving/building-fund-pledge';
import { fakeGivingApi, isBfPledgeHarnessActive } from '@/utils/bf-pledge-harness';

// The BF2026 pledge's calls to giving-api (`VUE_APP_GIVING_API_V2_URL`, routes under
// /pledges). Every response is wrapped as `{ data }`, which is unwrapped here, so
// callers get the payload. While the dev harness is on (/bf-pledge-states) the calls
// go to its fake instead, which speaks the same shapes.

const base = () => `${process.env.VUE_APP_GIVING_API_V2_URL}/pledges`;
const auth = token => ({ headers: { Authorization: `Bearer ${token}` } });

async function call(fake, real) {
  const response = isBfPledgeHarnessActive() ? await fake() : await real();
  return response?.data;
}

/** The member's pledge for the year (Pending, Pledged or Revised), or null. */
export function fetchPledge(token, year) {
  return call(
    () => fakeGivingApi.fetchPledge(year),
    async () => (await axios.get(`${base()}/confirm`, { ...auth(token), params: { year } })).data,
  );
}

/** `{ isEligible }` — false once the member (or their verified spouse) has pledged. */
export function fetchEligibility(token, year) {
  return call(
    () => fakeGivingApi.fetchEligibility(year),
    async () =>
      (await axios.get(`${base()}/pledgeEligibility`, { ...auth(token), params: { year } })).data,
  );
}

/** POST /pledges/confirm. giving-api answers 201 with an empty body. */
export function submitPledge(token, body) {
  return call(
    () => fakeGivingApi.submitPledge(body),
    async () => (await axios.post(`${base()}/confirm`, body, auth(token))).data,
  );
}

/** Years the member has a pledge for (their own, or a verified spouse's), any order. */
export function fetchPledgeYears(token) {
  return call(
    () => fakeGivingApi.fetchPledgeYears(),
    async () => (await axios.get(`${base()}/get-pledge-year`, auth(token))).data,
  );
}

/**
 * GET /bf/fulfilment: the pledge, the sow text and the BF giving that counts towards
 * them for the year (giving-api's BfFulfilmentView). Plain JSON, not `{ data }`, so
 * axios's own `data` is the view.
 */
export function fetchFulfilment(token, year) {
  const url = `${process.env.VUE_APP_GIVING_API_V2_URL}/bf/fulfilment`;
  return call(
    () => fakeGivingApi.fetchFulfilment(year),
    () => axios.get(url, { ...auth(token), params: { year } }),
  );
}

/**
 * DELETE /pledges/mine — testing aid for the /bf-pledge-states console. Clears the
 * signed-in member's own pledges for the year on the real giving-api, so the flow
 * can be walked again. giving-api answers it on staging only (404 elsewhere) and
 * returns bare `{ pledges, commitments }` counts, not the `{ data }` envelope.
 * Never goes to the harness: its "No pledge yet" scenario already covers the fake.
 */
export async function clearOwnPledges(token, year) {
  return (await axios.delete(`${base()}/mine`, { ...auth(token), params: { year } })).data;
}

const MEMBER_MESSAGES = Object.values(BF_PLEDGE_API_MESSAGES);

/**
 * What to tell the member about a failed call. The pledge routes answer every
 * failure as a 400 with its text in `errors[0]` — validation lists and server
 * faults' raw text included — so only the messages giving-api writes for members
 * are shown as they are. Everything else, and a lost connection, gets its own
 * wording.
 */
export function pledgeErrorMessage(error, fallback) {
  const message = error?.response?.data?.errors?.[0];
  if (MEMBER_MESSAGES.includes(message)) return message;
  if (!error?.response && error?.request) {
    return 'No internet connection. Please check your connection and try again.';
  }
  return fallback;
}
